/**
 * Сравнение карточек одного артикула по площадкам.
 *
 * Тексты для WB и OZON генерируются раздельно, поэтому у одного товара два
 * разных наименования, два описания и обычно разные цены. Переключаясь между
 * карточками, эту разницу не увидеть — только рядом.
 */

import React from 'react'
import { Badge } from '../../../../components/ui/Badge'
import { assertNever, MARKETPLACES } from '../../../../shared/marketplaces'
import type { MarketplaceCardDto } from '../../../pipeline/api'
import { MarketplacePhotos } from './MarketplacePhotos'
import { cardPhotos, cardPrice } from './marketplaceCardState'

const money = (value?: number | null) =>
  value == null ? '—' : `${Number(value).toLocaleString('ru-RU')} ₽`

const WB_TITLE_LIMIT = 60

/**
 * `MarketplaceCardDto['marketplace']` (`features/pipeline/api.ts`) — три
 * площадки конвейера карточек: `wb`, `ozon`, `ym` (Этап 2,
 * `docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §4). `switch` с `assertNever`
 * вместо тернарника — без риска молча увести новую площадку в ветку OZON,
 * если тип когда-нибудь расширится.
 */
type CompareMarketplace = MarketplaceCardDto['marketplace']

function compareBadgeTone(code: CompareMarketplace): 'purple' | 'blue' | 'yellow' | 'gray' {
  switch (code) {
    case 'wb': return 'purple'
    case 'ozon': return 'blue'
    case 'ym': return 'yellow'
    // Сайт — канал, а не площадка, но его карточки бэкенд отдаёт сюда же.
    case 'site': return 'gray'
    default: return assertNever(code)
  }
}

function compareShortLabel(code: CompareMarketplace): string {
  switch (code) {
    case 'wb': return 'WB'
    case 'ozon': return 'OZON'
    case 'ym': return MARKETPLACES.ym.label
    case 'site': return 'Сайт'
    default: return assertNever(code)
  }
}

/** Порядок площадок в сравнении: тот же, что в реестре (WB, OZON, Я.Маркет). */
function compareOrder(code: CompareMarketplace): number {
  switch (code) {
    case 'wb': return 0
    case 'ozon': return 1
    case 'ym': return 2
    case 'site': return 3
    default: return assertNever(code)
  }
}

/** Все ли значения списка равны — обобщение прежнего попарного сравнения на N площадок. */
function allEqual<T>(values: T[]): boolean {
  return values.every((value) => value === values[0])
}

interface Props {
  cards: MarketplaceCardDto[]
  article: string
  onBack: () => void
  onOpenVisual: (urls: string[], index: number, article: string) => void
}

export const MarketplaceCompare: React.FC<Props> = ({ cards, article, onBack, onOpenVisual }) => {
  const pair = cards
    .filter((card) => card.article === article)
    .sort((a, b) => compareOrder(a.marketplace) - compareOrder(b.marketplace))

  // Обобщение попарного сравнения на N площадок: раньше здесь предполагалось
  // ровно две карточки (wb + ozon) — с ym их может быть три.
  const titleOf = (card: MarketplaceCardDto) => card.revision_snapshot?.title || card.title || ''
  const differences: string[] = []
  if (pair.length >= 2) {
    if (!allEqual(pair.map(titleOf))) differences.push('наименование')
    if (!allEqual(pair.map((card) => card.revision_snapshot?.description || ''))) {
      differences.push('описание')
    }
    if (!allEqual(pair.map((card) => cardPrice(card)))) differences.push('цена')
    if (!allEqual(pair.map((card) => cardPhotos(card).length))) differences.push('количество фото')
  }

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800">
      <header className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3.5 py-2.5 dark:border-zinc-800">
        <span className="font-mono text-sm font-semibold">{article}</span>
        <Badge variant="gray">
          Различия: {differences.length > 0 ? differences.join(', ') : 'нет'}
        </Badge>
        <button
          type="button"
          onClick={onBack}
          className="ml-auto rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:text-zinc-300"
        >
          ← к обзору
        </button>
      </header>

      {pair.length < 2 ? (
        <p className="p-4 text-xs text-gray-500 dark:text-zinc-400">
          У этого артикула карточка есть только на одной площадке — сравнивать не с чем.
        </p>
      ) : (
        <div className={`grid grid-cols-1 gap-3 p-3.5 ${pair.length >= 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          {pair.map((card) => {
            const title = card.revision_snapshot?.title || card.title || null
            const description = card.revision_snapshot?.description || null
            const photos = cardPhotos(card)
            return (
              <article
                key={`${card.marketplace}-${card.seller_id}`}
                className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800"
              >
                <header className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-2.5 py-1.5 dark:border-zinc-800 dark:bg-zinc-900">
                  <Badge variant={compareBadgeTone(card.marketplace)}>
                    {compareShortLabel(card.marketplace)}
                  </Badge>
                  <span className="truncate text-2xs text-gray-500 dark:text-zinc-400">
                    {card.seller_name || `Кабинет ${card.seller_id}`}
                  </span>
                  <span className="ml-auto text-[0.8125rem] font-semibold">{money(cardPrice(card))}</span>
                </header>
                <div className="space-y-3 p-2.5">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xs font-semibold text-gray-500 dark:text-zinc-400">
                        Наименование
                      </span>
                      <span className="ml-auto text-2xs text-gray-400 dark:text-zinc-500">
                        {title
                          ? card.marketplace === 'wb'
                            ? `${title.length} из ${WB_TITLE_LIMIT}`
                            : `${title.length} символов`
                          : '—'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[0.8125rem] font-semibold leading-snug">{title || '—'}</p>
                  </div>

                  {description && (
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xs font-semibold text-gray-500 dark:text-zinc-400">
                          Описание
                        </span>
                        <span className="ml-auto text-2xs text-gray-400 dark:text-zinc-500">
                          {description.length} символов
                        </span>
                      </div>
                      <p className="mt-0.5 whitespace-pre-wrap text-xs leading-6 text-gray-600 dark:text-zinc-300">
                        {description}
                      </p>
                    </div>
                  )}

                  <div>
                    <span className="mb-1 block text-2xs font-semibold text-gray-500 dark:text-zinc-400">
                      Фото · {photos.length}
                    </span>
                    <MarketplacePhotos
                      urls={photos}
                      article={card.article}
                      limit={4}
                      size="sm"
                      onOpen={onOpenVisual}
                    />
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default MarketplaceCompare
